import * as React from 'react';
import * as I18n from 'i18n-js';
import ReverbPaymentsInput from './reverb_payments_input';
import ShopCurrencySelect from './shop_currency_select';
import bind from '@reverbdotcom/commons/dist/bind';
import InternalPayoutsFormFields from './internal_payouts_form_fields';
import Currency, {
  PAYPAL_SUPPORTED_CURRENCIES,
  INTERNAL_PAYOUTS_SUPPORTED_CURRENCIES,
  ADYEN_MARKETPAY_SUPPORTED_CURRENCIES,
} from '@reverbdotcom/commons/dist/currency';
import { ADYEN_MARKETPAY_SUPPORTED_COUNTRIES } from '@reverbdotcom/commons/dist/supported_countries';
import { PaymentProvider } from './payment_provider';
import ContactReverb from './contact_reverb';
import MarketpayPayoutsUpgradeAvailable from './marketpay_payouts_upgrade_available';
import MarketpayPayouts from './marketpay_payouts';
import PaymentProviderPaypalOption from './payment_provider_paypal_option';
import { isReverbPaymentsOnly } from './reverb_payments_only_policy';

interface PaypalProfileProps {
  paypalAccountLink: string;
  userAttributes: { firstName: string; lastName: string };
  formPrefix: string;
  errorMessages: string[];
  createdAt: string;
}

interface DirectCheckoutProfileAttributes {
  nameOnAccount: string;
  maskedNameSummary: string;
  bankName: string;
  countryCode: string;
  payoutMethod: string;
  hasAcceptedReverbBankTerms: boolean;
  profileErrors: string[];
  currency: string;
}

interface DirectCheckoutProfileProps {
  profileAttributes: DirectCheckoutProfileAttributes;
  hideBankAccountEditForm: boolean;
  dcProfileComplete: boolean;
  preferredSeller: boolean;
  hasCompletedPayouts: boolean;
  formPrefix: string;
  shopCurrency: string;
  hasAdyenBankAccount: boolean;
  bankChangesLocked: boolean;
  accountRepName: string;
  accountRepEmail: string;
}

export interface Props {
  alwaysAllowPaypal: boolean;
  directCheckoutFeePercent: number;
  directCheckoutProfileProps: DirectCheckoutProfileProps;
  paypalProfileProps: PaypalProfileProps;
  paymentProfileCreatedAt: string;
  paymentProvider: PaymentProvider;
  shopCurrency: string;
  shopCountryCode: string;
  shopLegalCountryCode: string;
  currencyErrors: string[];
  formPrefix: string;
}

interface State {
  reverbPaymentsCurrency: string;
  reverbPaymentsCountryCode: string;
  paymentProvider: PaymentProvider;
  shopCurrency: string;
}

export default class PayoutMethodFormFields extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      reverbPaymentsCountryCode: props.directCheckoutProfileProps.profileAttributes?.countryCode,
      reverbPaymentsCurrency: props.directCheckoutProfileProps.profileAttributes?.currencyCode,
      paymentProvider: props.paymentProvider,
      shopCurrency: props.shopCurrency,
    };
  }

  get directCheckoutProfile() {
    return {
      ...this.props.directCheckoutProfileProps.profileAttributes,
      countryCode: this.state.reverbPaymentsCountryCode,
      currency: this.state.reverbPaymentsCurrency,
    };
  }

  get isPaypalSelected() {
    return this.isPaypalOptionAvailable && this.state.paymentProvider === PaymentProvider.PayPal;
  }

  get isReverbPaymentsSelected() {
    return !this.isPaypalSelected;
  }

  get isPaypalOptionAvailable() {
    return this.props.alwaysAllowPaypal ||
      !isReverbPaymentsOnly({
        shopCountryCode: this.props.shopCountryCode,
        paymentProfileCreatedAt: this.props.paymentProfileCreatedAt,
      });
  }

  get shop() {
    return shopPayoutOptions({
      paymentProvider: this.state.paymentProvider,
      hasAdyenBankAccount: this.props.directCheckoutProfileProps.hasAdyenBankAccount,
      legalCountryCode: this.props.shopLegalCountryCode,
      countryCode: this.props.shopCountryCode,
      currencyCode: this.props.shopCurrency,
    });
  }

  get showCurrencySelect() {
    return this.isPaypalSelected ||
      this.props.shopCurrency === 'CAD' ||
      this.props.shopCountryCode === 'CA';
  }

  get availableShopCurrencies() {
    if (this.isPaypalSelected) {
      return PAYPAL_SUPPORTED_CURRENCIES;
    }

    return INTERNAL_PAYOUTS_SUPPORTED_CURRENCIES;
  }

  get supportedPaypalCurrencyOptions() {
    return this.availableShopCurrencies.map(currencyCodeToOption);
  }

  nameForField(fieldName: string) {
    return `${this.props.formPrefix}[${fieldName}]`;
  }

  @bind
  updateDirectCheckoutProfileCountryCode(reverbPaymentsCountryCode) {
    this.setState({ reverbPaymentsCountryCode });
  }

  @bind
  updateSelectedPayoutCurrency(currency) {
    this.setState({
      shopCurrency: currency,
      reverbPaymentsCurrency: currency,
    });
  }

  @bind
  handleChangePaymentMethod(paymentProvider: PaymentProvider) {
    this.setState({ paymentProvider });
  }

  internalReverbPaymentsPayouts() {
    return (
      <div
        className="dc-form"
        data-direct-checkout-profile-form
      >
        <div className="margined-left module panel mb-2">
          <InternalPayoutsFormFields
            dcProfileComplete={this.props.directCheckoutProfileProps.dcProfileComplete}
            formPrefix={this.props.directCheckoutProfileProps.formPrefix}
            hasCompletedPayouts={this.props.directCheckoutProfileProps.hasCompletedPayouts}
            hideBankAccountEditForm={this.props.directCheckoutProfileProps.hideBankAccountEditForm}
            onCountryChange={this.updateDirectCheckoutProfileCountryCode}
            onCurrencyChange={this.updateSelectedPayoutCurrency}
            preferredSeller={this.props.directCheckoutProfileProps.preferredSeller}
            profileAttributes={this.directCheckoutProfile}
            shopCurrency={this.state.shopCurrency}
            bankChangesLocked={this.props.directCheckoutProfileProps.bankChangesLocked}
            accountRepName={this.props.directCheckoutProfileProps.accountRepName}
            accountRepEmail={this.props.directCheckoutProfileProps.accountRepEmail}
          />
        </div>
      </div>
    );
  }

  reverbPaymentsProfile() {
    if (!this.props.directCheckoutProfileProps.hasAdyenBankAccount && this.shop.isCurrencySupportedForInternalPayouts) {
      return this.internalReverbPaymentsPayouts();
    }

    if (this.props.directCheckoutProfileProps.hasAdyenBankAccount && this.shop.isCurrencySupportedForInternalPayouts) {
      // In this scenario the seller set up Reverb Payments EU, then switched to Paypal + USD or CAD,
      // and now may want to set up internal payouts. This should be a manual process, we want to add friction here.
      return <ContactReverb />;
    }

    if (this.shop.isMarketpaySupportedCurrencyAndCountry) {
      return (
        <MarketpayPayouts
          hasAdyenBankAccount={this.props.directCheckoutProfileProps.hasAdyenBankAccount}
        />
      );
    }

    return null;
  }

  reverbPaymentsOption() {
    if (!this.shop.hasReverbPaymentsSupportedCurrency) {
      return null;
    }

    if (this.shop.hasEuroCurrencyWithUnsupportedMarketpayCountry) {
      // Captures the case of non European countries that use EUR, e.g. Hungarian seller using EUR payouts
      return null;
    }

    if (this.shop.isSupportedForMarketpayUpgrade) {
      return (
        <MarketpayPayoutsUpgradeAvailable
          bankCountryCode={this.props.shopCountryCode}
          sellingFeePercent={this.props.directCheckoutFeePercent}
          shopCurrency={this.props.shopCurrency}
        />
      );
    }

    return (
      <div>
        <ReverbPaymentsInput
          sellingFeePercent={this.props.directCheckoutFeePercent}
          shopCurrency={this.state.shopCurrency}
          bankCountryCode={this.state.reverbPaymentsCountryCode}
          onSelectReverbPayments={this.handleChangePaymentMethod}
          showRadioButton={this.isPaypalOptionAvailable}
          selected={this.isReverbPaymentsSelected}
          inputName={this.nameForField('payment_method')}
        />
        {this.isReverbPaymentsSelected &&
          this.reverbPaymentsProfile()
        }
      </div>
    );
  }

  render() {
    return (
      <div className="g-container shop-settings-payment-setup">
        {this.showCurrencySelect &&
          <ShopCurrencySelect
            defaultShopCurrencyCode={this.props.shopCurrency}
            shopCurrencyCode={this.state.shopCurrency}
            currencies={this.supportedPaypalCurrencyOptions}
            currencyErrors={this.props.currencyErrors}
            onCurrencyChange={this.updateSelectedPayoutCurrency}
          />
        }
        {this.reverbPaymentsOption()}
        {this.isPaypalOptionAvailable &&
          <PaymentProviderPaypalOption
            shopCurrency={this.state.shopCurrency}
            onPaymentProviderChange={this.handleChangePaymentMethod}
            errorMessages={this.props.paypalProfileProps.errorMessages}
            inputName={this.nameForField('payment_method')}
            paypalAccountLink={this.props.paypalProfileProps.paypalAccountLink}
            paypalProfileCreatedAt={this.props.paypalProfileProps.createdAt}
            selected={this.isPaypalSelected}
            paypalProfileFormPrefix={this.props.paypalProfileProps.formPrefix}
            userAttributes={this.props.paypalProfileProps.userAttributes}
          />
        }
      </div>
    );
  }
}

interface PayoutOptionsShop {
  paymentProvider: PaymentProvider;
  countryCode: string;
  legalCountryCode: string;
  currencyCode: string;
  hasAdyenBankAccount: boolean;
}

function shopPayoutOptions(shop: PayoutOptionsShop) {
  function legalCountryCode() {
    return shop.legalCountryCode || shop.countryCode;
  }

  function hasReverbPaymentsSupportedCurrency() {
    return INTERNAL_PAYOUTS_SUPPORTED_CURRENCIES.concat(ADYEN_MARKETPAY_SUPPORTED_CURRENCIES).includes(shop.currencyCode);
  }

  function isCurrencySupportedForInternalPayouts() {
    return INTERNAL_PAYOUTS_SUPPORTED_CURRENCIES.includes(shop.currencyCode);
  }

  function isMarketpaySupportedCurrencyAndCountry() {
    return ADYEN_MARKETPAY_SUPPORTED_COUNTRIES.includes(legalCountryCode()) &&
      ADYEN_MARKETPAY_SUPPORTED_CURRENCIES.includes(shop.currencyCode);
  }

  function isSupportedForMarketpayUpgrade() {
    return shop.paymentProvider === PaymentProvider.PayPal &&
      !shop.hasAdyenBankAccount &&
      isMarketpaySupportedCurrencyAndCountry();
  }

  // Captures the case of non European countries that use EUR, e.g. Hungarian (HU) seller using EUR payouts
  function hasEuroCurrencyWithUnsupportedMarketpayCountry() {
    return ADYEN_MARKETPAY_SUPPORTED_CURRENCIES.includes(shop.currencyCode) &&
      !ADYEN_MARKETPAY_SUPPORTED_COUNTRIES.includes(legalCountryCode());
  }

  return {
    isCurrencySupportedForInternalPayouts: isCurrencySupportedForInternalPayouts(),
    hasEuroCurrencyWithUnsupportedMarketpayCountry: hasEuroCurrencyWithUnsupportedMarketpayCountry(),
    isMarketpaySupportedCurrencyAndCountry: isMarketpaySupportedCurrencyAndCountry(),
    hasReverbPaymentsSupportedCurrency: hasReverbPaymentsSupportedCurrency(),
    isSupportedForMarketpayUpgrade: isSupportedForMarketpayUpgrade(),
  };
}

function currencyCodeToOption(currencyCode: string) {
  const name = I18n.t(`discovery.currencies.${currencyCode}`, { symbol: Currency.get(currencyCode).symbol });
  return { name, code: currencyCode };
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/payout_method_form_fields.tsx